import { isEmpty } from 'lodash';
import {
  CreateParams,
  CreateResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from 'react-admin';
import { getClientApi } from 'src/data-communication';
import {
  CreateOfferOrderRefundBodyDto,
  UpdateOfferOrderBodyDto,
  UpdateOfferOrderListStatusBodyDto,
  GetAccountBalanceListResponseDto,
  CreateOfferOrderFileExportBodyDto,
  FileExportDto,
  SortDirectionTypeDto,
  GetOfferOrderListSortByQueryParamTypeDto,
} from 'src/dtos';
import {
  mapOfferOrderCardPaymentDetailsDtoToModel,
  mapOfferOrderCardPaymentListDtoToModel,
  mapOfferOrderDetailsDtoToModel,
  mapOfferOrderDtoToModel,
} from 'src/mappers';
import { OfferOrderCardPayment, OfferOrderCardPaymentDetails, OfferOrderDetails } from 'src/models';

import { Logger } from '../utils/Logger';

const clientApi = getClientApi();

export const getOfferOrderList = async (params: GetListParams) => {
  const { pagination, sort, filter, meta } = params;
  const { data, total } = await clientApi.offerOrders.list({
    queryParams: {
      skip: pagination.perPage * (params.pagination.page - 1),
      take: pagination.perPage,
      sortBy: sort.field as GetOfferOrderListSortByQueryParamTypeDto,
      sortDirection: params.sort.order?.toLocaleLowerCase() as SortDirectionTypeDto,
      paymentType: filter.paymentType,
      status: Array.isArray(filter.status) ? filter.status.join(',') : filter.status,
      offerId: filter.offerId,
      accountId: filter.accountId,
      accountNumber: filter.accountNumber,
      accountType: filter.accountType,
      accountStatus: filter.accountStatus,
      firstName: filter.firstName,
      lastName: filter.lastName,
      email: filter.email,
    },
  });

  let balances: GetAccountBalanceListResponseDto = { data: [], total: 0 };

  try {
    const accountIds = data.map(anOfferOrder => anOfferOrder.accountId);

    if (!isEmpty(accountIds) && !meta?.skipBalance) {
      balances = await getClientApi().balance.list({ body: { accountIds } });
    }
  } catch (error) {
    Logger.error('Error occurred while getting account balance list');
    Logger.error(error);
  }

  return {
    data: data.map(anOfferOrder =>
      mapOfferOrderDtoToModel(
        anOfferOrder,
        balances.data.find(aBalance => aBalance.accountId === anOfferOrder.accountId),
      ),
    ),
    total: total || data.length || 0,
  };
};

export const getOfferOrderDetails = async (
  params: GetOneParams<OfferOrderDetails>,
): Promise<GetOneResult<OfferOrderDetails>> => {
  const data = await clientApi.offerOrders.retrieve({ params });

  return {
    data: mapOfferOrderDetailsDtoToModel(data),
  };
};

export const updateOfferOrderListStatus = async (
  params: UpdateManyParams<Omit<UpdateOfferOrderListStatusBodyDto, 'ids'>>,
): Promise<UpdateManyResult<{ id: string }>> => {
  const data = params.data as Omit<UpdateOfferOrderListStatusBodyDto, 'ids'>;
  await clientApi.offerOrders.updateListStatus({
    body: { ...data, ids: params.ids as string[] },
  });

  return {
    data: params.ids.map(id => id.toString()),
  };
};

export const updateOfferOrder = async ({
  data,
  id,
}: UpdateParams<UpdateOfferOrderBodyDto & { id: string }>): Promise<UpdateResult<OfferOrderDetails>> => {
  const response = await clientApi.offerOrders.update({ params: { id }, body: data });

  return {
    data: mapOfferOrderDetailsDtoToModel(response),
  };
};

export const deleteOfferOrder = async (
  params: DeleteParams<OfferOrderDetails>,
): Promise<DeleteResult<{ id: string }>> => {
  await clientApi.offerOrders.delete({ params });

  return {
    data: {
      id: params.id,
    },
  };
};

export const createOfferOrderRefund = async (
  params: CreateParams<CreateOfferOrderRefundBodyDto>,
): Promise<CreateResult<OfferOrderDetails>> => {
  const data = params.data as CreateOfferOrderRefundBodyDto & { offerOrderId: string };
  const response = await clientApi.offerOrders.createRefund({
    body: {
      mechanism: data.mechanism,
      adminNote: data.adminNote,
      bankName: data.bankName,
      reason: data.reason,
    },
    params: {
      id: params.meta.offerOrderId,
    },
  });

  return {
    data: mapOfferOrderDetailsDtoToModel(response),
  };
};

export const getOfferOrderCardPaymentList = async (
  params: GetListParams,
): Promise<GetListResult<OfferOrderCardPayment>> => {
  const response = await clientApi.offerOrders.listCardPayments({
    queryParams: {
      accountId: params.filter.accountId,
      skip: params.pagination.perPage * (params.pagination.page - 1),
      take: params.pagination.perPage,
    },
  });

  return {
    data: mapOfferOrderCardPaymentListDtoToModel(response.data),
    total: response.total,
  };
};

export const getOfferOrderCardPaymentDetails = async (
  params: GetOneParams,
): Promise<GetOneResult<OfferOrderCardPaymentDetails>> => {
  const response = await clientApi.offerOrders.retrieveCardPayment({
    params,
  });

  return {
    data: mapOfferOrderCardPaymentDetailsDtoToModel(response),
  };
};

export const createOfferOrderFileExport = async (
  params: CreateParams<CreateOfferOrderFileExportBodyDto>,
): Promise<CreateResult<FileExportDto>> => {
  const data = params.data as CreateOfferOrderFileExportBodyDto & { columns: string[] };
  const response = await clientApi.offerOrders.createFileExport({
    body: {
      columns: data.columns,
      name: data.name,
      offerId: data.offerId,
      status: data.status,
    },
  });

  return {
    data: response,
  };
};
